import { userAuthContextType } from '../../auth/AuthProvider';
import { axios, getResponse, settings } from '../../helpers/main';

const { backend } = settings;
const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectSerice = getResponse({ type: 0 }); //eslint-disable-line

/**
 * Service to manage communication with the Customers API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const CustomerService = {
    /**
     * Retrieves all Customers.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    getAll: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.customers}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.customers.getAll,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a Customer by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Customer ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    getById: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.customers}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.customers.getById,
                id: id,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Creates a new Customer.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.data - Customer data to create.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    create: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.customers}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing Customer.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Customer ID.
     * @param {Object} params.data - Customer data to update.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    update: async ({
        auth,
        id,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.customers}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a Customer by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Customer ID to delete.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.customers}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Retrieves external information for Customers.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    externalInfoAsync: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.customers}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.customers.externalInfo,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },
};
